import './index.css';
import { useCallback, forwardRef, useState, useEffect } from 'react';
import { useSelector as useSelector$1, useDispatch } from 'react-redux';
import { makeSetStoreField } from '@hh.ru/redux-create-reducer';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Link, Text, Input } from '@hh.ru/magritte-ui';
import { FormError } from 'bloko/blocks/form';
import InputText, { InputType } from 'bloko/blocks/inputText';
import Text$1 from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';
import { ArrowPathOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import Button from 'bloko/blocks/button';
import { ArrowPathScaleSmallKindDefault, IconColor } from 'bloko/blocks/icon';
import Link$1, { LinkIconPosition } from 'bloko/blocks/link';

const useSelector = useSelector$1;

const setRecaptcha = makeSetStoreField('recaptcha');
const setHhcaptcha = makeSetStoreField('hhcaptcha');
const emptyCaptcha = { isBot: false };
const useCaptcha = () => {
    const dispatch = useDispatch();
    const captchaKey = useSelector(({ hhcaptcha }) => hhcaptcha.captchaKey);
    const captchaText = useSelector(({ captchaText }) => captchaText);
    const hhCaptchaState = useSelector(({ hhcaptcha }) => hhcaptcha.captchaState);
    const reCaptchaState = useSelector(({ recaptcha }) => recaptcha.captchaState);
    const captchaState = hhCaptchaState || reCaptchaState;
    const addCaptchaParams = useCallback((data, text) => {
        if (data instanceof FormData) {
            captchaKey && data.append('captchaKey', captchaKey);
            captchaText && data.append('captchaText', captchaText);
            captchaState && data.append('captchaState', captchaState);
        }
        else {
            data.captchaText = text || captchaText;
            captchaKey && (data.captchaKey = captchaKey);
            captchaState && (data.captchaState = captchaState);
        }
    }, [captchaKey, captchaState, captchaText]);
    const resetCaptcha = useCallback(() => {
        dispatch(setRecaptcha(emptyCaptcha));
        dispatch(setHhcaptcha(emptyCaptcha));
    }, [dispatch]);
    const updateCaptcha = useCallback(({ recaptcha, hhcaptcha }) => {
        recaptcha && dispatch(setRecaptcha(recaptcha));
        hhcaptcha && dispatch(setHhcaptcha(hhcaptcha));
    }, [dispatch]);
    return { addCaptchaParams, resetCaptcha, updateCaptcha };
};

const fetchCaptchaKey = (fetcher, lang) => fetcher
    .post('/captcha', null, {
    params: { lang },
})
    .then((response) => response?.data?.key || '');

var CaptchaLang;
(function (CaptchaLang) {
    CaptchaLang["Russian"] = "RU";
    CaptchaLang["English"] = "EN";
})(CaptchaLang || (CaptchaLang = {}));

var styles = {"hhcaptcha-picture":"hhcaptcha-module_hhcaptcha-picture__-7tAb","hhcaptchaPicture":"hhcaptcha-module_hhcaptcha-picture__-7tAb","hhcaptcha-image-control":"hhcaptcha-module_hhcaptcha-image-control__VDFXl","hhcaptchaImageControl":"hhcaptcha-module_hhcaptcha-image-control__VDFXl","hhcaptcha-picture-container":"hhcaptcha-module_hhcaptcha-picture-container__wTR35","hhcaptchaPictureContainer":"hhcaptcha-module_hhcaptcha-picture-container__wTR35"};

const invertLang = (formLang) => {
    return formLang === CaptchaLang.Russian ? CaptchaLang.English : CaptchaLang.Russian;
};
const BlokoButton = forwardRef((props, _) => jsx(Button, { ...props }));
BlokoButton.displayName = 'BlokoButton';
const HhcaptchaImage = ({ trls, captchaUrl, getCaptcha, formLang, setFormLang, isMagritte }) => {
    const invertedLang = invertLang(formLang);
    return (jsxs(Fragment, { children: [jsx("img", { src: captchaUrl || undefined, className: styles.hhcaptchaPicture, "data-qa": "account-captcha-picture", alt: 'captcha' }), jsx(VSpacing, { base: 2 }), jsx("div", { className: styles.hhcaptchaImageControl, children: isMagritte ? (jsxs(Fragment, { children: [jsx(Link, { Element: BlokoButton, iconLeft: jsx(ArrowPathOutlinedSize16, {}), onClick: () => getCaptcha(false, formLang), "data-qa": "captcha-renew-text", typography: "label-3-regular", children: trls.renewText }), jsx(Link, { Element: BlokoButton, onClick: () => {
                                getCaptcha(false, invertedLang);
                                setFormLang(invertedLang);
                            }, "data-qa": "captcha-language", typography: "label-3-regular", children: trls.lang[invertedLang] })] })) : (jsxs(Fragment, { children: [jsx(Link$1, { Element: Button, icon: jsx(ArrowPathScaleSmallKindDefault, { highlighted: IconColor.Blue70, initial: IconColor.Blue50 }), iconPosition: LinkIconPosition.Left, onClick: () => getCaptcha(false, formLang), "data-qa": "captcha-renew-text", children: trls.renewText }), jsx(Link$1, { Element: Button, onClick: () => {
                                getCaptcha(false, invertedLang);
                                setFormLang(invertedLang);
                            }, "data-qa": "captcha-language", children: trls.lang[invertedLang] })] })) })] }));
};

const captchaAction = makeSetStoreField('hhcaptcha');
const captchaTextAction = makeSetStoreField('captchaText');
const Hhcaptcha = ({ trls, lang, fetcher, isMagritte = false, size = 'medium' }) => {
    const dispatch = useDispatch();
    const hhcaptcha = useSelector(({ hhcaptcha }) => hhcaptcha);
    const [captchaUrl, setCaptchaUrl] = useState('');
    const [captchaText, setCaptchaText] = useState('');
    const [formLang, setFormLang] = useState(CaptchaLang.Russian);
    const getCaptchaImage = useCallback(async (error = false, formLang) => {
        const captchaKey = await fetchCaptchaKey(fetcher, lang || formLang);
        dispatch(captchaAction({
            isBot: true,
            captchaKey,
            captchaState: hhcaptcha.captchaState,
            captchaError: error,
        }));
        setCaptchaUrl(`/captcha/picture?key=${captchaKey}`);
    }, [dispatch, hhcaptcha.captchaState, lang, fetcher]);
    const getCaptcha = useCallback(async (formLang = CaptchaLang.Russian) => {
        if (!hhcaptcha.isBot) {
            setCaptchaText('');
            return;
        }
        if (!hhcaptcha.captchaKey) {
            await getCaptchaImage(hhcaptcha.captchaError || false, formLang);
        }
        setCaptchaText('');
    }, [getCaptchaImage, hhcaptcha]);
    useEffect(() => {
        void getCaptcha(formLang);
    }, [formLang, getCaptcha]);
    useEffect(() => {
        dispatch(captchaTextAction(captchaText));
    }, [captchaText, dispatch]);
    const renderContent = () => {
        if (isMagritte) {
            return (jsxs(Fragment, { children: [jsx(VSpacing, { base: 4 }), jsx(Text, { typography: "label-3-regular", style: "primary", Element: "span", children: trls.description }), jsx(VSpacing, { base: 3 }), jsx(HhcaptchaImage, { trls: trls, formLang: formLang, captchaUrl: captchaUrl, getCaptcha: getCaptchaImage, setFormLang: setFormLang, isMagritte: true }), jsx(VSpacing, { base: 4 }), jsx(Input, { name: "captchaText", value: captchaText, onChange: (value) => {
                            setCaptchaText(value);
                        }, size: size, type: InputType.Text, "data-qa": "account-captcha-input", spellCheck: "false", autoCorrect: "off", autoCapitalize: "off", autoComplete: "off", placeholder: trls.placeholder }), jsx(VSpacing, { base: 2 })] }));
        }
        return (jsxs(Fragment, { children: [jsx(VSpacing, { base: 4 }), jsx(Text$1, { Element: "span", children: trls.description }), jsx(VSpacing, { base: 3 }), jsxs("div", { className: styles.hhcaptchaPictureContainer, children: [jsx(HhcaptchaImage, { trls: trls, formLang: formLang, captchaUrl: captchaUrl, getCaptcha: getCaptchaImage, setFormLang: setFormLang }), jsx(VSpacing, { base: 4 }), jsx(InputText, { name: "captchaText", value: captchaText, onChange: (value) => {
                                setCaptchaText(value);
                            }, type: InputType.Text, "data-qa": "account-captcha-input", spellCheck: "false", autoCorrect: "off", autoCapitalize: "off", autoComplete: "off", placeholder: trls.placeholder })] }), jsx(VSpacing, { base: 2 })] }));
    };
    return (jsxs("div", { className: !hhcaptcha.isBot ? 'g-hidden' : '', children: [renderContent(), jsx(FormError, { show: hhcaptcha.captchaError || false, "data-qa": "account-captcha-error", children: trls.error })] }));
};

export { Hhcaptcha, HhcaptchaImage, useCaptcha };
//# sourceMappingURL=index.js.map
